import Button from '@doordash/component-button'
import {
  PasswordField,
  PhoneField,
} from '@doordash/component-fields'
import {
  Text,
  Colors,
  Spacing,
  Stack,
  StackChildren,
} from '@doordash/design-language'
import Separator from '@doordash/component-separator'
import * as Exp from '../../../experimentConstants'
import { FeatureFlag } from '@doordash/lib-feature-flags'
import * as React from 'react'
import * as actions from '../../../actions'
import { applicationCountrySelector } from '../../../selectors'
import { push, Steps } from '../../../utilities/steps'
import { validators as sharedValidators } from '../../../utilities/validators'

import { get, noop } from 'lodash'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import * as events from '../../../events'
import Form from '../../../utilities/Form'
import { ReferralLink } from './styles'
import { rextrkPixel, trackingTagGeneration } from './trackingTagGeneration'
import { ProfileSetup as Tag } from '../../../models/selectorAnchors'
import NamesEntryForm from '../names-entry-form'
import OfferLetter from '../../organisms/offer-letter'
import t from './strings'

type Props = {
  uniqueLink: string
  application?: any
  isLoggedIn?: boolean
  vehicleTypes?: any
  submitPersonalInfo?: (
    uniqueLink: any,
    data: any,
    application: any,
    isLoggedIn: any,
    vehicleTypes: any,
    offerLetterSigned: boolean
  ) => Promise<any>
  trackProfileFormRendered?: (any) => Promise<any>
  trackReferrerButtonClicked?: (any) => Promise<any>
  trackSubmitProfile?: (application: any, data: any) => Promise<any>
  phoneCountry?: string
  shouldShowOfferLetter?: boolean
  selectedApplicationCountry?: { [k:string]: boolean }
}

type ProfileState = {
  middleNameInputDisabled: boolean
  isShowingReferrerField: boolean
  nationalPhoneNumber: string
  phoneCountry: string
}

export class ProfileSetup extends React.Component<
  Props & RouteComponentProps<any>,
  ProfileState
> {
  public static fields: { [k: string]: string } = {
    firstName: '',
    altFirstName: '',
    middleName: '',
    lastName: '',
    altLastName: '',
    password: '',
    referredBy: '',
  }

  public static validators: any = {
    firstName: sharedValidators.firstName,
    altFirstName: noop,
    middleName: sharedValidators.middleName,
    lastName: sharedValidators.lastName,
    altLastName: noop,
    referredBy: sharedValidators.hidden,
    password: sharedValidators.password,
  }

  public static defaultProps = {
    application: {},
    submitPersonalInfo: noop,
    trackProfileFormRendered: noop,
    trackReferrerButtonClicked: noop,
    trackSubmitProfile: noop,
    isLoggedIn: false,
    vehicleTypes: {},
    phoneCountry: 'US',
    shouldShowOfferLetter: false,
    selectedApplicationCountry: {
      isUS: true,
      isCA: false,
      isAU: false,
      isJP: false,
    }
  }

  public state = {
    middleNameInputDisabled: false,
    isShowingReferrerField: false,
    nationalPhoneNumber: '',
    phoneCountry: '',
  }

  public componentDidMount() {
    const { application, trackProfileFormRendered, selectedApplicationCountry } = this.props
    trackProfileFormRendered(application)
    trackingTagGeneration()

    if (selectedApplicationCountry.isJP) {
      ProfileSetup.validators.firstName = sharedValidators.shortFirstName
      ProfileSetup.validators.altFirstName = sharedValidators.shortFirstName
      ProfileSetup.validators.lastName = sharedValidators.shortLastName
      ProfileSetup.validators.altLastName = sharedValidators.shortLastName
    }
  }

  public handleSubmit = async data => {
    const {
      submitPersonalInfo,
      uniqueLink,
      application,
      isLoggedIn,
      vehicleTypes,
      history,
      trackSubmitProfile,
      shouldShowOfferLetter,
    } = this.props

    try {
      await submitPersonalInfo(
        uniqueLink,
        data,
        application,
        isLoggedIn,
        vehicleTypes,
        shouldShowOfferLetter
      )
      trackSubmitProfile(application, data)

      push(uniqueLink, Steps.BackgroundCheck, history)
    } catch (error) {
      throw error
    }
  }

  public toggleMiddleName = () => {
    this.setState(state => ({
      middleNameInputDisabled: !state.middleNameInputDisabled,
    }))
  }

  public toggleReferralField = () => {
    this.props.trackReferrerButtonClicked(!this.state.isShowingReferrerField)
    this.populateReferralValidators(!this.state.isShowingReferrerField)
    this.setState(state => ({
      isShowingReferrerField: !state.isShowingReferrerField,
    }))
  }

  public populateReferralValidators = isShowingReferrerField => {
    ProfileSetup.validators = {
      ...ProfileSetup.validators,
      referredBy: isShowingReferrerField
        ? sharedValidators.optionalPhoneNumber
        : sharedValidators.hidden,
    }
  }

  public render() {
    const { middleNameInputDisabled, isShowingReferrerField } = this.state
    const { application, shouldShowOfferLetter, selectedApplicationCountry } = this.props

    const finalFields = { ...ProfileSetup.fields }

    if (middleNameInputDisabled || selectedApplicationCountry.isJP) {
      // override middleName validator when disabled
      ProfileSetup.validators.middleName = () => null
    } else {
      ProfileSetup.validators.middleName = sharedValidators.middleName
    }

    if (get(application, 'hasConsumerAccount') === true) {
      delete finalFields.password
    }

    if (
      get(application, 'referredById', null) !== null ||
      get(application, 'is_guarenteed_earnings')
    ) {
      delete finalFields.referredBy
    }

    return (
      <React.Fragment>
        {rextrkPixel}
        <StackChildren size={Spacing.Large}>
          <Text color={Colors.SystemRed60} styles={Text.Styles.MajorPageTitle}>
            {t.h1()}
          </Text>
          <Text
            lineHeights={Text.LineHeights.XxxLarge}
            color={Text.Colors.TextTertiary}
            weight={Text.Weights.Regular}
          >
            {t.instructions()}
          </Text>
          <Form
            onSubmit={this.handleSubmit}
            validators={ProfileSetup.validators}
            fields={finalFields}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => (
              <form onSubmit={handleSubmit}>
                <StackChildren size={Spacing.XLarge}>
                  <NamesEntryForm
                    fields={fields}
                    handleChange={handleChange.bind(this)}
                    middleNameInputDisabled={middleNameInputDisabled}
                    toggleMiddleName={this.toggleMiddleName.bind(this)}
                  />
                  {(fields.referredBy && !selectedApplicationCountry.isJP) && (
                    <ReferralLink
                      tag="a"
                      color={Text.Colors.SystemRed60}
                      styles={Text.Styles.PageSubtext}
                      onClick={this.toggleReferralField}
                      data-anchor-id={Tag.ReferralToggle}
                    >
                      {t.referralToggle()}
                    </ReferralLink>
                  )}
                  {fields.referredBy && isShowingReferrerField && (
                    <PhoneField
                      isInternationalEnabled
                      type="tel"
                      onChange={(unformatted, formatted) => {
                        this.setState(state => ({
                          nationalPhoneNumber: unformatted,
                        }))
                        handleChange('referredBy', formatted.number.number)
                      }}
                      onCountryChange={(
                        countrySelected,
                        unformatted,
                        formatted
                      ) => {
                        this.setState(state => ({
                          phoneCountry: countrySelected,
                          nationalPhoneNumber: unformatted,
                        }))
                        handleChange('referredBy', formatted.number.number)
                      }}
                      autoComplete="tel"
                      label={t.referrerPhoneLabel()}
                      isLabelHidden
                      value={this.state.nationalPhoneNumber}
                      currentCountry={
                        this.state.phoneCountry
                          ? this.state.phoneCountry
                          : this.props.phoneCountry
                      }
                      description={t.optional()}
                    />
                  )}
                </StackChildren>
                {fields.password && (
                  <StackChildren size={Spacing.Large}>
                    <Stack size={Spacing.XxxLarge} />
                    <Separator type={Separator.Types.FullBorder} />
                    <Text
                      size={Text.Sizes.S18}
                      weight={Text.Weights.Regular}
                      color={Text.Colors.SystemGrey60}
                    >
                      {t.h2()}
                    </Text>
                    <PasswordField
                      label={t.password()}
                      onChange={handleChange.bind(this, 'password')}
                      description={t.passwordDescription()}
                      autoComplete="new-password"
                      strings={{
                        show: t.passwordShow(),
                        hide: t.passwordHide(),
                      }}
                      data-anchor-id={Tag.PasswordField}
                      {...fields.password}
                    />
                  </StackChildren>
                )}
                {shouldShowOfferLetter && (
                  <FeatureFlag
                    on={() => <OfferLetter showSeparator={true} />}
                    flagName={Exp.W_MODEL_EXPERIMENT}
                  />
                )}
                <StackChildren size={Spacing.XLarge}>
                  <Stack size={Spacing.Large} />
                  <Button
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                    onClick={async () => {
                      try {
                        await handleSubmit()
                      } catch (e) {
                        console.warn(e)
                      }
                    }}
                    data-anchor-id={Tag.PrimaryAction}
                  >
                    {t.continue()}
                  </Button>
                </StackChildren>
              </form>
            )}
          </Form>
        </StackChildren>
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  application: state.application,
  isLoggedIn: state.isLoggedIn,
  vehicleTypes: state.vehicleTypes,
  phoneCountry: state.countryShortname,
  selectedApplicationCountry: applicationCountrySelector(state),
})

const mapDispatchToProps = {
  submitPersonalInfo: actions.submitPersonalInfo,
  trackProfileFormRendered: events.trackProfileFormRendered,
  trackReferrerButtonClicked: events.trackReferrerButtonClicked,
  trackSubmitProfile: events.trackSubmitProfile,
}
export default withRouter<Props & RouteComponentProps<any>>(
  connect(mapStateToProps, mapDispatchToProps)(ProfileSetup)
)
